import { useCallback, useEffect, useState, useContext } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Drawer from '@material-ui/core/Drawer'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import List from '@material-ui/core/List'
import CssBaseline from '@material-ui/core/CssBaseline'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import MenuIcon from '@material-ui/icons/Menu'
import { BrowserRouter, Route, Redirect, Switch, Link } from 'react-router-dom'
import { getRoutes } from './router'
import { Button, ListSubheader } from '@material-ui/core'
import ListItemLink from './components/drawer/ListItemLink'
import logoImage from './assets/img/brand/conway_claim_small.jpg'
import ExitToAppOutlinedIcon from '@material-ui/icons/ExitToAppOutlined'
import AuthContext from './msal/msal'
import { getMyUserManagement } from './api/userManagement.service'
import { userRoles } from './common/constants'
import NotAuthorized from './views/NotAuthorized/NotAuthorized'
import Tooltip from '@material-ui/core/Tooltip'
import PhoneOutlined from '@material-ui/icons/Phone'
import MailOutlined from '@material-ui/icons/MailOutlined'
import { useSelector, useDispatch } from 'react-redux'
import AccountChange from './views/General/AccountChange'
import Alert from './components/Alert/Alert'
import WelcomeMessageModal from './views/General/WelcomeMessageModal'
import { getProviders } from './api/providers.service'
import { setProvider } from './redux/features/providerSlice'
import { toggleModalFullscreen, toggleModalFullscreenOption } from './redux/features/modalSlice'
import { getProviderConditions } from './api/providerConditions.service'

const drawerWidth = 200

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex'
  },
  appBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: theme.zIndex.drawer + 1,
    borderBottom: 'solid red 1px'
  },
  menuButton: {
    marginRight: 36
  },
  logo: {
    marginRight: 30
  },
  hide: {
    display: 'none'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap'
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    borderRight: 'solid red 1px'
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: 'hidden',
    width: theme.spacing(0) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(7) + 1,
      borderRight: 'solid red 1px'
    }
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(10, 2)
  },
  listSubHeaderRoot: {
    color: 'red'
  },
  changeProviderLink: {
    color: '#73818f',
    textDecoration: 'none',
    '&:hover': {
      color: '#303336',
      textDecoration: 'none'
    }
  }
}))

const PrivateRoute = ({ children, userRole, protectedRole, ...rest }) => {
  const providerSelected = useSelector((state) => state.provider)

  return (
    <Route
      {...rest}
      render={({ location }) =>
        !protectedRole || userRole === protectedRole ? (
          providerSelected.id !== undefined && providerSelected.id !== null ? (
            children
          ) : (
            <Redirect
              to={{
                pathname: '/accountchange',
                state: { from: location }
              }}
            />
          )
        ) : (
          <Redirect
            to={{
              pathname: '/not-authorized',
              state: { from: location }
            }}
          />
        )
      }
    />
  )
}

export default function App() {
  const classes = useStyles()
  const [open, setOpen] = useState(window.innerWidth > 450)
  const [userRole, setUserRole] = useState(null)
  const msal = useContext(AuthContext)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const providerSelected = useSelector((state) => state.provider)

  const permissions = providerSelected.providerPermissions || {}
  const routesToHide = [
    ...(!providerSelected.urlCatalog ? ['/general-catalog'] : []),
    ...(!permissions.showMyReports ? ['/reports'] : []),
    ...(!permissions.showIrpIncidences ? ['/irp-incidences'] : []),
    ...(!permissions.showNonConformity ? ['/non-conformity-incidences'] : []),
    ...(!permissions.showMyOrders ? ['/incidences'] : [])
  ]
  const routes = getRoutes(routesToHide)
  const dispatch = useDispatch()

  const handleLogout = useCallback(() => {
    msal.signOut().then()
  }, [msal])

  const checkUserRole = useCallback(async () => {
    try {
      const resp = await getMyUserManagement()
      setUserRole(resp.data.role)
    } catch (err) {
      handleLogout()
    }
  }, [handleLogout])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  })

  const updateSelectedProvider = useCallback(async () => {
    if (providerSelected.id === undefined) return

    getProviders({
      page: 0,
      pageSize: 1,
      filtered: [
        {
          id: 'id',
          value: providerSelected.id
        }
      ]
    }).then(({ data }) => {
      const provider = data.results[0]
      if (provider === undefined) {
        dispatch(setProvider({}))
        return
      }

      getProviderConditions(provider.id).then((result) => {
        dispatch(
          setProvider(result.data.every((condition) => condition.isAccepted) ? provider : {})
        )
      })
    })
  }, [providerSelected.id, dispatch])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  })

  useEffect(() => {
    checkUserRole()
    updateSelectedProvider()
  }, [checkUserRole, updateSelectedProvider])

  const setModalFullscreenByWindowWidth = useCallback(() => {
    if (windowWidth <= 540) {
      dispatch(toggleModalFullscreen(true))
      dispatch(toggleModalFullscreenOption(false))
    } else {
      dispatch(toggleModalFullscreenOption(true))
    }
  }, [windowWidth, dispatch])
  useEffect(() => {
    setModalFullscreenByWindowWidth()
  }, [windowWidth, setModalFullscreenByWindowWidth])

  const handleDrawerOpenClose = () => {
    setOpen(!open)
  }

  return (
    <div className={classes.root}>
      <WelcomeMessageModal />
      {!!userRole ? (
        userRole === userRoles.CLIENT ? (
          <NotAuthorized role={userRole} logOut={handleLogout} />
        ) : (
          <>
            <CssBaseline />
            <BrowserRouter>
              <AppBar className={classes.appBar} elevation={0} position="fixed" color="inherit">
                <Toolbar>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpenClose}
                    edge="start"
                  >
                    <MenuIcon />
                  </IconButton>
                  <img className={classes.logo} src={logoImage} alt="" height={40} />

                  <Tooltip
                    title={
                      providerSelected.id !== undefined && providerSelected.id !== null
                        ? 'Cambiar de proveedor'
                        : 'Debe seleccionar un proveedor'
                    }
                  >
                    <Link to="/accountchange" className={classes.changeProviderLink}>
                      <Typography
                        className="text-capitalize"
                        variant={windowWidth < 450 ? 'body1' : 'h6'}
                      >
                        {providerSelected.id !== undefined && providerSelected.id !== null
                          ? `${providerSelected.name} (${providerSelected.id})`
                          : 'Seleccione un Proveedor'}
                      </Typography>
                    </Link>
                  </Tooltip>
                </Toolbar>
                <div className="my-auto mr-md-4 d-flex">
                  {providerSelected.buyerPhone !== undefined &&
                    providerSelected.buyerPhone !== null &&
                    providerSelected.buyerPhone !== '' && (
                      <Tooltip
                        title={
                          windowWidth < 960
                            ? `Teléfono de contacto: ${providerSelected.buyerPhone}`
                            : 'Teléfono de contacto'
                        }
                      >
                        <Button
                          variant="text"
                          color="secondary"
                          size="small"
                          href={`tel:${providerSelected.buyerPhone}`}
                          target="_blank"
                          className="mr-0 mr-lg-2 text-lowercase"
                          startIcon={<PhoneOutlined color="secondary" fontSize={'small'} />}
                          style={{ minWidth: 'fit-content' }}
                        >
                          {windowWidth < 960 ? '' : providerSelected.buyerPhone}
                        </Button>
                      </Tooltip>
                    )}
                  {providerSelected.buyerEmail !== undefined &&
                    providerSelected.buyerEmail !== null &&
                    providerSelected.buyerEmail !== '' && (
                      <Tooltip
                        title={
                          windowWidth < 960
                            ? `Correo de contacto: ${providerSelected.buyerEmail}`
                            : 'Correo de contacto'
                        }
                      >
                        <Button
                          variant="text"
                          color="secondary"
                          size="small"
                          href={`mailto:${providerSelected.buyerEmail}`}
                          target="_blank"
                          className="mr-0 mr-lg-2 text-lowercase"
                          startIcon={<MailOutlined color="secondary" fontSize={'small'} />}
                          style={{ minWidth: 'fit-content' }}
                        >
                          {windowWidth < 960 ? '' : providerSelected.buyerEmail}
                        </Button>
                      </Tooltip>
                    )}
                  <Alert />
                  <Button
                    variant={windowWidth < 960 ? 'text' : 'outlined'}
                    color="secondary"
                    size="small"
                    onClick={handleLogout}
                    startIcon={<ExitToAppOutlinedIcon fontSize={'small'} color="secondary" />}
                    style={{ minWidth: 'fit-content' }}
                  >
                    {windowWidth < 960 ? '' : 'Logout'}
                  </Button>
                </div>
              </AppBar>
              <Drawer
                variant="permanent"
                className={clsx(classes.drawer, {
                  [classes.drawerOpen]: open,
                  [classes.drawerClose]: !open
                })}
                classes={{
                  paper: clsx({
                    [classes.drawerOpen]: open,
                    [classes.drawerClose]: !open
                  })
                }}
              >
                <div className={classes.toolbar} />
                <Divider />
                {routes
                  .filter((route) => !route.protected || route.protected === userRole)
                  .map((section, index) => (
                    <List
                      key={`section-${index}`}
                      dense
                      subheader={
                        section.children && !section.path ? (
                          <ListSubheader
                            component="li"
                            key={`ListSubheader-${index}`}
                            classes={{ root: !open ? classes.hide : classes.listSubHeaderRoot }}
                          >
                            {section.title}
                          </ListSubheader>
                        ) : null
                      }
                    >
                      {!section.children && section.path
                        ? section.isDrawerItem && (
                            <ListItemLink
                              key={index}
                              to={section.path}
                              primary={section.title}
                              icon={section.icon}
                              onClick={(_) => updateSelectedProvider()}
                            />
                          )
                        : section.children
                            .filter((route) => !route.protected || route.protected === userRole)
                            .map((item, subIndex) => {
                              return (
                                item.isDrawerItem && (
                                  <div key={`subSection-${index}-${item.title}`}>
                                    <ListItemLink
                                      key={subIndex}
                                      to={item.path}
                                      primary={item.title}
                                      icon={item.icon}
                                      onClick={(_) => updateSelectedProvider()}
                                    />
                                  </div>
                                )
                              )
                            })}
                    </List>
                  ))}
              </Drawer>
              <main className={classes.content}>
                <Switch>
                  {routes.map((route, index) =>
                    !route.children ? (
                      <PrivateRoute
                        key={index}
                        path={route.path}
                        exact={route.exact}
                        children={<route.view />}
                        userRole={userRole}
                        protectedRole={routes.protected}
                      />
                    ) : (
                      route.children.map((routeChildren, indexChildren) => (
                        <PrivateRoute
                          key={`${index}.${indexChildren}`}
                          path={routeChildren.path}
                          exact={routeChildren.exact}
                          children={<routeChildren.view />}
                          userRole={userRole}
                          protectedRole={route.protected}
                        />
                      ))
                    )
                  )}
                  <Route
                    key={'not-auth'}
                    path={'/not-authorized'}
                    exact={true}
                    children={<NotAuthorized />}
                  />
                  <Route
                    key={'not-provider-selected'}
                    path={'/accountchange'}
                    exact={true}
                    children={<AccountChange />}
                  />
                </Switch>
              </main>
            </BrowserRouter>
          </>
        )
      ) : (
        ''
      )}
    </div>
  )
}
